import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import moment from "moment";
import authService from "services/authService";
import LOG from "services/loggingService";
import voicemailService from "services/voicemailService";
import errorCallbackFactory from "util/errorCallbackFactory";
import AudioPlayerView from "viewModels/widgets/audio/AudioPlayerView";
import ViewModel from "viewModels/ViewModel";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import RecordingRestoreDetailsView from "./RecordingRestoreDetailsView";
import { LONG_DATE_OPTIONS } from "./../util/DateTimeHelper";

const AcdVoicemailView = ViewModel.extend(function (voicemailId, options) {
    const self = this;
    const POLLING_INTERVAL = (options && options.pollingInterval) || 3000;
    const MAX_RETRIES = (options && options.maxRetries) || 10;
    const isSparkV4FTEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1128_IDUI_SPARK_V4
    );

    self.httpErrorStatus = ko.observable(-1);
    const handleError = function (errorResponse) {
        if (
            errorResponse &&
            errorResponse.details &&
            errorResponse.details.status === 404
        ) {
            self.isDeleted(true);
        }
        LOG.info("Error loading acd voicemail", errorResponse);
    };

    // Get voicemail media
    const getVoicemailMedia = function (voicemailId, options) {
        const formatId = options && options.formatId;

        // Poll while response is 202. Need a 200.
        return new Promise(function (resolve, reject) {
            const _getVoicemailMedia = function (voicemailId, retryCount) {
                voicemailService
                    .getVoicemailMedia(voicemailId, formatId)
                    .then(function (voicemailResponse) {
                        if (
                            voicemailResponse &&
                            voicemailResponse.mediaFileUri
                        ) {
                            resolve(voicemailResponse);
                        } else if (retryCount > 0) {
                            self.showLongRunningMessage(true);
                            setTimeout(function () {
                                _getVoicemailMedia(voicemailId, retryCount - 1);
                            }, POLLING_INTERVAL);
                        } else {
                            LOG.info(
                                "Reached maximum retries for loading acdVoicemail",
                                {
                                    maxRetries: MAX_RETRIES,
                                }
                            );
                            reject(
                                new Error("Maximum retry limit exceeded", {
                                    cause: "MAX_RETRY_LIMIT_EXCEEDED",
                                })
                            );
                        }
                    })
                    .catch(handleError);
            };
            _getVoicemailMedia(voicemailId, MAX_RETRIES - 1);
        });
    };

    const voicemailMetadata = ko.observable();

    const audioOptions = _.assign(
        {
            autoplay: false,
            darkMode: false,
            showAnnotations: false,
            isVoicemail: true,
        },
        _.omit(options, "displayed")
    );

    self.displayed = ko.observable(options ? !!options.displayed : false);

    self.voicemail = ko.observable();
    self.isVoicemail = true;
    const isAudioElementRendered = ko.observable(false);
    self.audioRendered = function () {
        isAudioElementRendered(true);
    };

    // <!-- Need to implement some otherwise unused properties for RecordingView interface
    const readOnlyObservableFalse = self.computed(function () {
        return false;
    });
    self.audioPlayerView = self.subView(
        AudioPlayerView,
        null,
        voicemailId,
        audioOptions,
        voicemailId
    );
    self.hasAnnotationText = readOnlyObservableFalse;
    self.hasChat = readOnlyObservableFalse;
    self.hasMessages = readOnlyObservableFalse;
    self.hasScreenRecording = readOnlyObservableFalse;
    self.isRecordingAvailable = readOnlyObservableFalse;
    self.isAvailableAudioRecordingWithMissingMedia = readOnlyObservableFalse;
    self.recordingRestoreDetails = self.subView(
        RecordingRestoreDetailsView,
        ko.computed(() => {
            return {
                ...voicemailMetadata(),
                media: "voicemail",
            };
        }),
        {
            afterRender: self.audioRendered,
            header: ko.observable(self.audioPlayerView.header),
        }
    );
    // --> end of RecordingView vars

    self.isAcdVoicemail = true;
    self.isWorking = ko.observable(false);
    self.showDeleteConfirmation = ko.observable(false);
    self.deleteVoicemail = function () {
        self.isWorking(true);
        self.showDeleteConfirmation(false);
        return voicemailService
            .deleteVoicemail(voicemailId)
            .then(function () {
                self.voicemail(null);
                self.hasAudio(false);
                self.isDeleted(true);
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "playback.acdVoicemailDelete",
                })
            )
            .finally(function () {
                self.isWorking(false);
            });
    };
    self.keep = function () {
        self.isWorking(true);
        const policy = { voicemailRetentionPolicyType: "RETAIN_INDEFINITELY" };
        return voicemailService
            .updateVoicemailRetention(voicemailId, policy)
            .then(function (voicemailResponse) {
                voicemailMetadata(voicemailResponse);
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "playback.acdVoicemailKeep",
                })
            )
            .finally(function () {
                self.isWorking(false);
            });
    };
    self.editingDownload = ko.observable(false);
    self.editDownload = function () {
        self.editingDownload(true);
    };
    self.cancelDownloadEdit = function () {
        self.editingDownload(false);
    };
    self.fileFormatOptions = ko.observable([
        { text: ko.i18n.renderString("conversationDetails.mp3"), value: "mp3" },
        { text: ko.i18n.renderString("conversationDetails.wav"), value: "wav" },
        {
            text: ko.i18n.renderString("conversationDetails.webm"),
            value: "webm",
        },
    ]);
    self.selectedFileFormat = ko.observable(
        isSparkV4FTEnabled && self.fileFormatOptions()
            ? self.fileFormatOptions()[0]?.value
            : undefined
    );
    self.downloading = ko.observable(false);
    self.download = function () {
        self.downloading(true);
        getVoicemailMedia(voicemailId, { formatId: self.selectedFileFormat() })
            .then(function (voicemail) {
                const link = document.getElementsByClassName(
                    "conversation-download-voicemail"
                );
                link[0].href = ko.unwrap(voicemail).mediaFileUri;
                link[0].click();
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "conversationDetails.downloadAcdVoicemail",
                })
            )
            .finally(function () {
                self.downloading(false);
            });
    };
    self.confirmDelete = function () {
        self.showDeleteConfirmation(true);
    };

    self.hasAudio = ko.observable(false);
    self.isRecordingLoading = ko.observable(true);
    self.showLongRunningMessage = ko.observable(false);
    self.recordingRequestError = ko.observable(false);
    self.isDeleted = ko.observable(false);
    self.canDelete = authService.hasAnyAuthorization([
        "perm:voicemail:acdVoicemail:delete",
    ]);
    self.recordingDeletedTitle = "playback.voicemailIsDeleted.title";
    self.recordingDeletedDescription =
        "playback.voicemailIsDeleted.description";

    self.deleteDate = self.computed(function () {
        let calculatedDeleteDate = null;
        const vmMetadata = ko.unwrap(voicemailMetadata);
        if (vmMetadata && vmMetadata.deleteRetentionPolicy) {
            const deleteRetentionPolicyType =
                vmMetadata.deleteRetentionPolicy.voicemailRetentionPolicyType;
            const ttlDays = vmMetadata.deleteRetentionPolicy.numberOfDays || 0;
            if (
                deleteRetentionPolicyType === "RETAIN_WITH_TTL" &&
                vmMetadata.deletedDate
            ) {
                calculatedDeleteDate = moment(vmMetadata.deletedDate)
                    .add(ttlDays, "days")
                    .toDate();
            } else if (
                deleteRetentionPolicyType === "RETAIN_WITH_TTL" ||
                deleteRetentionPolicyType === "IMMEDIATE_DELETE"
            ) {
                calculatedDeleteDate = moment(vmMetadata.createdDate)
                    .add(60 + ttlDays, "days")
                    .toDate();
            }
            // else if policy is KEEP_INDEFINITELY, calculatedDeleteDate = null
        }
        return calculatedDeleteDate;
    });

    self.formattedDeleteDate = self.computed(() =>
        self.deleteDate()
            ? ko.i18n.formatDate(
                  self.deleteDate(),
                  null,
                  null,
                  LONG_DATE_OPTIONS
              )
            : null
    );

    _.assign(self, audioOptions);

    // Get voicemail metadata
    voicemailService
        .getVoicemail(voicemailId)
        .then(function (voicemailResponse) {
            voicemailMetadata(voicemailResponse);
        })
        .catch(handleError);

    getVoicemailMedia(voicemailId, { formatId: "mp3" })
        .then(function (voicemail) {
            self.voicemail(voicemail);
        })
        .catch(function (error) {
            errorCallbackFactory.createCallback({
                namespace: "playback.acdVoicemailRequest",
            })(error);
            self.recordingRequestError(true);
            self.httpErrorStatus(error.details?.status);
        })
        .finally(function () {
            self.isRecordingLoading(false);
        });

    self.computed(function () {
        if (self.voicemail()) {
            self.hasAudio(!!self.voicemail().mediaFileUri);
            if (isAudioElementRendered()) {
                const unwrapped = ko.toJS(self.voicemail);
                self.audioPlayerView.setChannels([
                    {
                        audioUri: unwrapped.mediaFileUri,
                        waveformData: unwrapped.waveformData,
                        waveformUri: unwrapped.waveUri,
                    },
                ]);
            }
        }
    });

    self.pause = function () {
        if (self.audioPlayerView.isPlaying()) {
            self.audioPlayerView.pause();
        }
    };

    self.play = function () {
        if (self.audioPlayerView.isPlaying()) {
            self.audioPlayerView.play();
        }
    };
});

export default AcdVoicemailView;
